import type { ClientUiPublic } from '../api/platformConfig'
import { effectiveAspectRatios } from './clientUiImageModel'
import type { ComicPanelAspectPreset } from './comicDomainTypes'

export type { ComicPanelAspectPreset } from './comicDomainTypes'

/** 与后端 ComicPanelAspectCodes 保持一致 */
export const COMIC_PANEL_ASPECT_VALUES = ['3:4', '2:3', '1:1', '4:3', '16:9'] as const

export const COMIC_PANEL_ASPECT_OPTIONS: {
  value: (typeof COMIC_PANEL_ASPECT_VALUES)[number]
  label: string
  description: string
}[] = [
  { value: '3:4', label: '竖版 (3:4)', description: '常用漫画单页比例，适合竖屏阅读' },
  { value: '2:3', label: '竖版 (2:3)', description: '略窄的竖版比例' },
  { value: '1:1', label: '正方形 (1:1)', description: '方形画布，适合对称构图' },
  { value: '4:3', label: '横版 (4:3)', description: '传统横屏比例' },
  { value: '16:9', label: '横版 (16:9)', description: '宽屏横版，适合电影感分镜' },
]

export const COMIC_PANEL_ASPECT_PLACEHOLDER = {
  value: '',
  label: '请选择单页比例',
  description: '画布预览、阅读器与整页生图均依赖此项',
} as const

/** 下拉选中值：合法则保留，否则为空（不猜测默认比例） */
export function panelAspectSelectValue(current: string): string {
  const trimmed = current.trim()
  if (trimmed && isValidComicPanelAspect(trimmed)) return trimmed
  return ''
}

export function panelAspectSelectOptions() {
  return [COMIC_PANEL_ASPECT_PLACEHOLDER, ...COMIC_PANEL_ASPECT_OPTIONS]
}

/** @deprecated 画册单页比例以 COMIC_PANEL_ASPECT_OPTIONS 为准；模型 aspect_ratio 仅用于生图兼容校验 */
export function panelAspectOptionsFromClientUi(
  clientUi: ClientUiPublic,
  modelId: string,
  platformId: string,
) {
  return effectiveAspectRatios(clientUi, modelId, platformId) ?? []
}

/** 将 "3:4" 等比例转为 Tailwind aspect 类；无法解析时不猜测默认比例 */
export function panelAspectTailwindClass(panelAspect: ComicPanelAspectPreset): string {
  const m = /^(\d+):(\d+)$/.exec(panelAspect.trim())
  if (m) return `aspect-[${m[1]}/${m[2]}]`
  return 'aspect-auto'
}

export function panelAspectCssValue(panelAspect: ComicPanelAspectPreset): string | undefined {
  const m = /^(\d+):(\d+)$/.exec(panelAspect.trim())
  if (!m) return undefined
  return `${m[1]} / ${m[2]}`
}

export function panelAspectToApiRatio(panelAspect: ComicPanelAspectPreset): string {
  return panelAspect.trim()
}

export function isValidComicPanelAspect(panelAspect: ComicPanelAspectPreset): boolean {
  return COMIC_PANEL_ASPECT_VALUES.includes(
    panelAspect.trim() as (typeof COMIC_PANEL_ASPECT_VALUES)[number],
  )
}

export function requireValidComicPanelAspect(
  panelAspect: ComicPanelAspectPreset,
): { ok: true; value: ComicPanelAspectPreset } | { ok: false; message: string } {
  const trimmed = panelAspect.trim()
  if (!isValidComicPanelAspect(trimmed)) {
    return { ok: false, message: '请选择单页比例' }
  }
  return { ok: true, value: trimmed }
}
